/* eslint-disable no-debugger */
import { ApiHttpService, ApiHttpServiceNewApi } from '../ApiHttpService';
import { AddressSerializer } from '../serializers/AddressSerializer';
import { AddressSuggestionSerializer } from '../serializers/AddressSuggestionSerializer';
import { StreetSuggestionSerializer } from '../serializers/StreetSuggestionSerializer';
import { Address, AddressNewApi } from '../../models/Address';
import { ParamsHelper } from '../../helpers/ParamsHelper';
import Locations from '../../common/address-form/Locations';

const ADDRESS_PATH = 'usuario/direcciones';
const STREET_SUGGESTION_PATH = 'Calles/ObtenerSugerenciaCalles';
const ADDRESS_RESOLUTION_PATH = 'Calles/ObtenerCalles';
const DELETE_ADDRESS_PATH = 'usuario/BorrarDireccion';

const NEWAPI_STREET_SUGGESTION_PATH = 'Address/search-street';
const NEWAPI_STREET_SUGGESTION_ELASTIC_PATH = 'StreetES/street-suggestion';
const NEWAPI_ADDRESS_PATH = 'Address';

class AddressController {
  static async all() {
    const authenticated = true;
    const response = await ApiHttpService.get(ADDRESS_PATH, {}, authenticated);
    const deSerializedData = (response.data || []).map(AddressSerializer.deSerialize);
    return deSerializedData.map(data => new Address(data));
  }

  static serializeAddress(address) {
    let field1 = null;
    if (address.extraInfoType1 && address.extraInfoLine1) {
      field1 = {
        Campo: {
          Id: address.extraInfoType1,
        },
        Valor: address.extraInfoLine1,
      };
    }
    let field2 = null;
    if (address.extraInfoType2 && address.extraInfoLine2) {
      field2 = {
        Campo: {
          Id: address.extraInfoType2,
        },
        Valor: address.extraInfoLine2,
      };
    }
    let userSetCoordinates = null;
    if (address.userSetCoordinates) {
      userSetCoordinates = {
        Latitud: Locations[address.location] ?
          Locations[address.location].latitud : -34.90445321195559,
        Longitud: Locations[address.location] ?
          Locations[address.location].longitud : -56.18669757538199,
      };
    } else {
      userSetCoordinates = {
        Latitud: Locations[address.location] ?
          Locations[address.location].latitud : -34.90445321195559,
        Longitud: Locations[address.location] ?
          Locations[address.location].longitud : -56.18669757538199,
      };
    }
    let coordinates = null;
    if (address.coordinates) {
      coordinates = {
        Latitud: address.coordinates.latitude,
        Longitud: address.coordinates.longitude,
      };
    }
    return {
      Ampliacion: address.additionalData,
      Apartamento: address.apartment,
      Bis: address.bis,
      Calle: {
        Localidad: {
          Departamento: {
            ID: address.department,
          },
          ID: address.location,
        },
        Nombre: address.street,
      },
      Campo1: field1,
      Campo2: field2,
      Esquina1: {
        Nombre: address.corner1,
      },
      Esquina2: !address.corner2 ? null : {
        Nombre: address.corner2,
      },
      ID: address.id || 0,
      Letra: address.letter,
      MapaPunto: coordinates,
      NombreDireccion: address.addressType,
      MapaCliente: userSetCoordinates,
      IDCliente: address.userId,
      NumeroPuerta: address.doorNumber,
    };
  }

  static async create(address) {
    const authenticated = true;
    const serializedAddress = AddressController.serializeAddress(address);
    const response = await ApiHttpService.post(ADDRESS_PATH, serializedAddress, authenticated);
    const deSerializedData = AddressSerializer.deSerialize(response.data);
    return new Address(deSerializedData);
  }

  static async edit(address) {
    const authenticated = true;
    const serializeAddress = AddressController.serializeAddress(address);
    const response = await ApiHttpService.post(ADDRESS_PATH, serializeAddress, authenticated);
    const deSerializedData = AddressSerializer.deSerialize(response.data);
    return new Address(deSerializedData);
  }

  static async delete(addressId) {
    const authenticated = true;
    const params = ParamsHelper.createQueryParams({
      direccion: addressId,
    });
    return ApiHttpService.delete(`${DELETE_ADDRESS_PATH}${params}`, {}, authenticated);
  }

  static async streetSuggestion(text, location, department) {
    const queryParams = ParamsHelper.createQueryParams({
      calle: text,
      localidad: location,
      departamento: department,
    });
    const response = await ApiHttpService.post(`${STREET_SUGGESTION_PATH}${queryParams}`);
    return (response.data || []).map(StreetSuggestionSerializer.deSerialize);
  }

  static async streetSuggestionV2(text, location, department) {
    const queryParams = ParamsHelper.createQueryParams({
      query: text,
      locality: location,
      department,
    });
    const response = await ApiHttpServiceNewApi.get(`${NEWAPI_STREET_SUGGESTION_ELASTIC_PATH}${queryParams}`);
    return (response.data ? response.data.data : []).map(StreetSuggestionSerializer.deSerialize);
  }

  static async determineAddress(street, doorNumber, city = '', department = '') {
    const queryParams = ParamsHelper.createQueryParams({
      localidad: city,
      departamento: department,
      nropuerta: doorNumber,
      calle: street,
    });
    const response = await ApiHttpService.post(`${ADDRESS_RESOLUTION_PATH}${queryParams}`);
    const deSerializedData = AddressSuggestionSerializer.deSerialize(response.data);
    return deSerializedData;
  }

  static serializeAddressNewApi(address) {
    let firstStreet = null;
    let firstStreetName = null;
    let secondStreet = null;
    let secondStreetName = null;
    if (address.corner1) {
      firstStreet = typeof address.corner1 !== 'string' ? address.corner1.id : 0;
      firstStreetName = typeof address.corner1 !== 'string' ? address.corner1.name : address.corner1;
    }

    if (address.corner2) {
      secondStreet = typeof address.corner2 !== 'string' ? address.corner2.id : 0;
      secondStreetName = typeof address.corner2 !== 'string' ? address.corner2.name : address.corner2;
    }
    return {
      id: address.id,
      type: address.addressType,
      mainStreet: typeof address.street !== 'string' ? address.street.id : 0,
      mainStreetName: typeof address.street !== 'string' ? address.street.name : address.street,
      doorNumber: address.doorNumber,
      letter: address.letter,
      bis: address.bis,
      apartment: address.apartment,
      firstStreet,
      firstStreetName,
      secondStreet,
      secondStreetName,
      locality: address.location,
      department: address.department,
      description: address.additionalData,
      locationMap: {
        latitude: address.coordinates.latitude,
        longitude: address.coordinates.longitude,
      },
    };
  }

  static async allNewApi() {
    const authenticated = true;
    const response = await ApiHttpServiceNewApi.get(NEWAPI_ADDRESS_PATH, {}, authenticated);
    return response.data.data.map(data => new AddressNewApi(data));
  }

  static async createNewApi(address) {
    const authenticated = true;
    const serializedAddress = AddressController.serializeAddressNewApi(address);
    const response = await ApiHttpServiceNewApi.post(
      NEWAPI_ADDRESS_PATH,
      serializedAddress, authenticated,
    );
    return new AddressNewApi(response.data.data);
  }

  static async editNewApi(address) {
    const authenticated = true;
    const serializeAddress = AddressController.serializeAddressNewApi(address);
    const response = await ApiHttpServiceNewApi.put(
      NEWAPI_ADDRESS_PATH,
      serializeAddress, authenticated,
    );
    return new AddressNewApi(response.data.data);
  }

  static async streetSuggestionNewApi(text, location) {
    const queryParams = {
      query: text,
      locality: location,
    };
    const response = await ApiHttpServiceNewApi.get(`${NEWAPI_STREET_SUGGESTION_PATH}`, queryParams, true);
    return (response.data.data ? response.data.data.filter(x => !/\s*\d+$/.test(x.name)) : []).map(StreetSuggestionSerializer.deSerializeNewApi);
  }
}

export { AddressController };



// WEBPACK FOOTER //
// ./src/networking/controllers/AddressController.js